import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { contentList, categories } from '@/mocks/content';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const article = contentList.find((item) => item.id === id);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">内容不存在</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedItems = contentList
    .filter((item) => item.category === article.category && item.id !== article.id)
    .slice(0, 4);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://japstream.github.io' },
      { '@type': 'ListItem', position: 2, name: category?.name || '', item: `https://japstream.github.io/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://japstream.github.io/article/${article.id}` },
    ],
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${article.title} - 中文字幕${article.categoryLabel}推荐`,
    description: article.description,
    datePublished: article.updateDate,
    author: { '@type': 'Organization', name: '中文字幕日韩影视导航' },
    publisher: { '@type': 'Organization', name: '中文字幕日韩影视导航' },
  };

  const faqJsonLd = article.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  } : null;

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />
        {faqJsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
          />
        )}

        <nav className="bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              </li>
              <li>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </div>
              </li>
              <li>
                <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                  {category?.name}
                </Link>
              </li>
              <li>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </div>
              </li>
              <li className="text-foreground-700 font-medium truncate">{article.title}</li>
            </ol>
          </div>
        </nav>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[900px] mx-auto px-4 md:px-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.categoryLabel}
              </span>
              {article.genre.map((g) => (
                <span key={g} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                  {g}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-8">
              <span>{article.country} · {article.year}年</span>
              <span>|</span>
              <span>{article.episodeCount}</span>
              <span>|</span>
              <span className="flex items-center gap-1">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-star-fill text-accent-500 text-xs"></i>
                </div>
                <strong className="text-foreground-800">{article.rating}</strong>
              </span>
              <span>|</span>
              <span className="text-primary-500 font-medium">{article.subtitleStatus}</span>
            </div>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10 bg-background-100">
              <img
                src={article.image}
                alt={`${article.title} 中文字幕${article.categoryLabel}`}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <div
                  className="prose-custom text-sm text-foreground-700 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.longDescription }}
                />

                {article.faq.length > 0 && (
                  <div className="mt-10">
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                      常见问题
                    </h3>
                    <div className="space-y-2">
                      {article.faq.map((f, i) => (
                        <div key={i} className="rounded-lg bg-background-100 overflow-hidden">
                          <button
                            className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                            onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                          >
                            <span className="text-sm font-medium text-foreground-900 pr-4">{f.question}</span>
                            <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full transition-transform duration-300 ${
                              activeFaq === i ? 'rotate-180 bg-primary-100 text-primary-500' : 'bg-background-200 text-foreground-500'
                            }`}>
                              <i className="ri-arrow-down-s-line text-xs"></i>
                            </div>
                          </button>
                          <div className={`transition-all duration-300 overflow-hidden ${
                            activeFaq === i ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0'
                          }`}>
                            <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">{f.answer}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-heading font-semibold text-foreground-950 text-sm mb-4">
                      基本信息
                    </h4>
                    <dl className="space-y-3">
                      <div className="flex justify-between">
                        <dt className="text-xs text-foreground-500">国家/地区</dt>
                        <dd className="text-xs text-foreground-800 font-medium">{article.country}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-xs text-foreground-500">年份</dt>
                        <dd className="text-xs text-foreground-800 font-medium">{article.year}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-xs text-foreground-500">集数/片长</dt>
                        <dd className="text-xs text-foreground-800 font-medium">{article.episodeCount}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-xs text-foreground-500">字幕状态</dt>
                        <dd className="text-xs text-primary-500 font-medium">{article.subtitleStatus}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-xs text-foreground-500">字幕速度</dt>
                        <dd className="text-xs text-foreground-800 font-medium">{article.updateSpeed}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-heading font-semibold text-foreground-950 text-sm mb-4">
                      主演
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {article.cast.map((actor) => (
                        <span key={actor} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs">
                          {actor}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-heading font-semibold text-foreground-950 text-sm mb-4">
                      观看平台
                    </h4>
                    <div className="space-y-3">
                      {article.platforms.map((p) => (
                        <a
                          key={p.name}
                          href={p.url}
                          className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-200/30 transition-colors cursor-pointer"
                          rel="nofollow"
                        >
                          <div className="flex items-center gap-2">
                            {p.hasSubtitle && (
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-checkbox-circle-fill text-primary-500 text-xs"></i>
                              </div>
                            )}
                            <span className="text-sm text-foreground-800 font-medium">{p.name}</span>
                          </div>
                          <span className="text-xs text-foreground-500">{p.speed}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>

            {relatedItems.length > 0 && (
              <div className="mt-14 pt-10 border-t border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-6">
                  更多中文字幕{article.categoryLabel}推荐
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {relatedItems.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group cursor-pointer"
                      data-product-shop
                    >
                      <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-100">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {item.title}
                      </h4>
                      <div className="flex items-center gap-1 mt-1">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        </div>
                        <span className="text-xs text-foreground-600">{item.rating}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}